import React, { useState, useEffect } from 'react';
import { API } from '../services/api';
import GlobalHeader from '../components/GlobalHeader';
import StatCard from '../components/stats/StatCard';
import StatsChart from '../components/stats/StatsChart';
import TopLinksTable from '../components/stats/TopLinksTable';
import SignatureStatsTable from '../components/stats/SignatureStatsTable';
import './StatisticsPage.css';

const PERIODS = [
    { value: '24h', label: '24 heures' },
    { value: '7d', label: '7 jours' },
    { value: '30d', label: '30 jours' },
    { value: '90d', label: '90 jours' }
];

function StatisticsPage() {
    const [period, setPeriod] = useState('7d');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [overview, setOverview] = useState(null);
    const [topLinks, setTopLinks] = useState([]);
    const [signaturesStats, setSignaturesStats] = useState([]);

    useEffect(() => {
        loadAllStats();
    }, [period]);

    const loadAllStats = async () => {
        setLoading(true);
        setError(null);

        try {
            const [overviewRes, linksRes, signaturesRes] = await Promise.all([
                API.getStatisticsOverview(period),
                API.getTopLinks(period, 10),
                API.getSignaturesStats(period)
            ]);

            if (overviewRes?.success) {
                setOverview(overviewRes.data);
            }
            if (linksRes?.success) {
                setTopLinks(linksRes.data.links || []);
            }
            if (signaturesRes?.success) {
                setSignaturesStats(signaturesRes.data.signatures || []);
            }
        } catch (err) {
            console.error('Error loading stats:', err);
            setError('Erreur lors du chargement des statistiques');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="statistics-page">
                <GlobalHeader />
                <div className="statistics-content">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Chargement des statistiques...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="statistics-page">
                <GlobalHeader />
                <div className="statistics-content">
                    <div className="error-container">
                        <p>{error}</p>
                        <button onClick={loadAllStats} className="btn-primary">
                            Réessayer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="statistics-page">
            <GlobalHeader />
            <div className="statistics-content">
                <div className="page-header">
                    <h1>Statistiques</h1>
                    <div className="period-selector">
                        {PERIODS.map((p) => (
                            <button
                                key={p.value}
                                className={`period-btn ${period === p.value ? 'active' : ''}`}
                                onClick={() => setPeriod(p.value)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* KPIs */}
                <div className="stats-grid">
                    <StatCard
                        title="Impressions"
                        value={overview?.totals?.impressions || 0}
                        change={overview?.changes?.impressions}
                        icon="👁️"
                    />
                    <StatCard
                        title="Clics"
                        value={overview?.totals?.clicks || 0}
                        change={overview?.changes?.clicks}
                        icon="🖱️"
                    />
                    <StatCard
                        title="Taux de clic"
                        value={overview?.totals?.ctr || 0}
                        suffix="%"
                        change={overview?.changes?.ctr}
                        icon="📊"
                    />
                    <StatCard
                        title="Clics uniques"
                        value={overview?.totals?.unique_clicks || 0}
                        icon="👤"
                    />
                </div>

                {/* Graphique d'évolution */}
                <div className="chart-section">
                    <StatsChart
                        data={overview?.timeline || []}
                        title="Évolution sur la période"
                    />
                </div>

                {/* Sections côte à côte */}
                <div className="tables-grid">
                    <TopLinksTable links={topLinks} />
                    <SignatureStatsTable signatures={signaturesStats} />
                </div>
            </div>
        </div>
    );
}

export default StatisticsPage;
